/**
 * Dashboard Pulse — React Query hook for the unified dashboard endpoint
 *
 * Calls zaviago_backend.api.dashboard_pulse.get_dashboard_pulse
 * which returns pulse metrics, needs_attention items, discovery prompts,
 * and tool_activity flags in a single request.
 */

import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { frappeMethod } from "@/lib/erp/utils";
import { useAuthStore } from "@/lib/store/authStore";

// ── Types matching backend response shape ──────────────────

export interface PulseItem {
  id: string;
  label: string;
  value: number;
  delta: number | null;
  href: string;
  is_zero?: boolean;
}

export interface NeedsAttentionItem {
  id: string;
  title: string;
  count: number;
  href: string;
  priority: "high" | "medium" | "low";
}

export interface DiscoveryPrompt {
  id: string;
  message: string;
  cta: string;
  href: string;
}

export interface ToolActivity {
  has_orders_today: boolean;
  has_revenue_today: boolean;
  has_unread_chatwoot: boolean;
  has_crm_leads: boolean;
  has_low_stock: boolean;
}

export interface DashboardPulseResponse {
  pulse: PulseItem[];
  needs_attention: NeedsAttentionItem[];
  discovery: DiscoveryPrompt | null;
  tool_activity: ToolActivity;
}

// ── Fetcher ───────────────────────────────────────────────

async function fetchDashboardPulse(): Promise<DashboardPulseResponse> {
  return frappeMethod<DashboardPulseResponse>(
    "zaviago_backend.api.dashboard_pulse.get_dashboard_pulse"
  );
}

// ── Hook ───────────────────────────────────────────────────

export function useGetDashboardPulse(): UseQueryResult<DashboardPulseResponse, Error> {
  // Works in both auth modes: direct (frappeClient) and control_plane (tenantServerUrl)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const hasHydrated = useAuthStore((s) => s.hasHydrated);

  return useQuery({
    queryKey: ["dashboard-pulse"],
    queryFn: fetchDashboardPulse,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
    enabled: hasHydrated && isAuthenticated,
  });
}
